import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  Award,
  Target,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Briefcase,
  Crown,
  Star
} from "lucide-react";

const MLMDashboard = ({ userType = "reseller" }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [commissionData, setCommissionData] = useState({
    totalEarnings: 15847.50,
    thisMonth: 3245.75,
    lastMonth: 2890.25,
    pendingPayouts: 1250.00,
    teamSize: 47,
    directReferrals: 12,
    totalSales: 89450.00,
    level: "Gold"
  });

  const [teamPerformance, setTeamPerformance] = useState([
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah@example.com",
      level: "Silver",
      sales: 12450.00,
      commission: 3112.50,
      teamSize: 8,
      joinDate: "2024-01-15",
      status: "active"
    },
    {
      id: 2,
      name: "Mike Chen",
      email: "mike@example.com",
      level: "Bronze",
      sales: 8750.00,
      commission: 2187.50,
      teamSize: 5,
      joinDate: "2024-02-03",
      status: "active"
    },
    {
      id: 3,
      name: "Lisa Rodriguez",
      email: "lisa@example.com",
      level: "Gold",
      sales: 18900.00,
      commission: 4725.00,
      teamSize: 15,
      joinDate: "2023-11-22",
      status: "active"
    }
  ]);

  const [commissionHistory, setCommissionHistory] = useState([
    {
      id: 1,
      date: "2024-01-30",
      type: "Direct Sale",
      customer: "John Smith",
      orderAmount: 52.97,
      commission: 13.24,
      status: "paid"
    },
    {
      id: 2,
      date: "2024-01-29",
      type: "Team Sale (Level 2)",
      customer: "Jane Doe",
      orderAmount: 79.99,
      commission: 7.99,
      status: "paid"
    },
    {
      id: 3,
      date: "2024-01-28",
      type: "Volume Bonus",
      customer: "Team Performance",
      orderAmount: 0,
      commission: 125.00,
      status: "pending"
    },
    {
      id: 4,
      date: "2024-01-27",
      type: "Direct Sale",
      customer: "Bob Wilson",
      orderAmount: 52.97,
      commission: 13.24,
      status: "paid"
    },
    {
      id: 5,
      date: "2024-01-26",
      type: "Team Sale (Level 3)",
      customer: "Amy Brown",
      orderAmount: 52.97,
      commission: 2.65,
      status: "paid"
    }
  ]);

  const getLevelBadgeColor = (level) => {
    const colors = {
      'Bronze': 'bg-orange-100 text-orange-800',
      'Silver': 'bg-gray-100 text-gray-800',
      'Gold': 'bg-yellow-100 text-yellow-800',
      'Platinum': 'bg-purple-100 text-purple-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const getLevelIcon = (level) => {
    const icons = {
      'Bronze': <Award className="h-4 w-4" />,
      'Silver': <Star className="h-4 w-4" />,
      'Gold': <Crown className="h-4 w-4" />,
      'Platinum': <Crown className="h-4 w-4" />
    };
    return icons[level] || <Award className="h-4 w-4" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">MLM Dashboard</h1>
          <p className="text-gray-600">Track your commissions, team, and performance</p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge className={`${getLevelBadgeColor(commissionData.level)} flex items-center space-x-1`}>
            {getLevelIcon(commissionData.level)}
            <span>{commissionData.level} Level</span>
          </Badge>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="commissions">Commissions</TabsTrigger>
          <TabsTrigger value="team">My Team</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="tools">Marketing Tools</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Key Metrics */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Earnings</p>
                    <p className="text-2xl font-bold text-green-600">
                      ${commissionData.totalEarnings.toLocaleString()}
                    </p>
                    <div className="flex items-center mt-1">
                      <ArrowUpRight className="h-3 w-3 text-green-600" />
                      <span className="text-xs text-green-600 ml-1">12.3% vs last month</span>
                    </div>
                  </div>
                  <div className="bg-green-100 p-3 rounded-lg">
                    <DollarSign className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">This Month</p>
                    <p className="text-2xl font-bold text-blue-600">
                      ${commissionData.thisMonth.toLocaleString()}
                    </p>
                    <div className="flex items-center mt-1">
                      <ArrowUpRight className="h-3 w-3 text-blue-600" />
                      <span className="text-xs text-blue-600 ml-1">8.7% vs last month</span>
                    </div>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Team Size</p>
                    <p className="text-2xl font-bold text-purple-600">{commissionData.teamSize}</p>
                    <div className="flex items-center mt-1">
                      <ArrowUpRight className="h-3 w-3 text-purple-600" />
                      <span className="text-xs text-purple-600 ml-1">3 new this month</span>
                    </div>
                  </div>
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Pending Payout</p>
                    <p className="text-2xl font-bold text-orange-600">
                      ${commissionData.pendingPayouts.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Next payout: Feb 1</p>
                  </div>
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <Calendar className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Performance Chart */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Commission Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div>
                      <p className="font-medium text-green-900">Direct Sales</p>
                      <p className="text-sm text-green-700">Personal commission earnings</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-900">${(commissionData.thisMonth * 0.6).toFixed(2)}</p>
                      <p className="text-xs text-green-600">60% of total</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div>
                      <p className="font-medium text-blue-900">Team Commissions</p>
                      <p className="text-sm text-blue-700">Multi-level earnings</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-blue-900">${(commissionData.thisMonth * 0.3).toFixed(2)}</p>
                      <p className="text-xs text-blue-600">30% of total</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div>
                      <p className="font-medium text-purple-900">Bonuses</p>
                      <p className="text-sm text-purple-700">Volume and performance bonuses</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-purple-900">${(commissionData.thisMonth * 0.1).toFixed(2)}</p>
                      <p className="text-xs text-purple-600">10% of total</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {commissionHistory.slice(0, 5).map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.type}</p>
                        <p className="text-sm text-gray-600">{item.customer}</p>
                        <p className="text-xs text-gray-500">{new Date(item.date).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">+${item.commission}</p>
                        <Badge 
                          variant={item.status === 'paid' ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {item.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Commissions Tab */}
        <TabsContent value="commissions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Commission History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">Type</th>
                      <th className="text-left p-3">Customer/Source</th>
                      <th className="text-left p-3">Order Amount</th>
                      <th className="text-left p-3">Commission</th>
                      <th className="text-left p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissionHistory.map((item) => (
                      <tr key={item.id} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-gray-600">
                          {new Date(item.date).toLocaleDateString()}
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-xs">
                            {item.type}
                          </Badge>
                        </td>
                        <td className="p-3 font-medium">{item.customer}</td>
                        <td className="p-3">
                          {item.orderAmount > 0 ? `$${item.orderAmount}` : '-'}
                        </td>
                        <td className="p-3 font-semibold text-green-600">
                          +${item.commission}
                        </td>
                        <td className="p-3">
                          <Badge 
                            variant={item.status === 'paid' ? 'default' : 'secondary'}
                            className={item.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}
                          >
                            {item.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">Commission Structure</h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
                  <div>
                    <p><strong>Level 1 (Direct):</strong> 25% commission</p>
                    <p><strong>Level 2:</strong> 10% commission</p>
                    <p><strong>Level 3:</strong> 5% commission</p>
                  </div>
                  <div>
                    <p><strong>Volume Bonus:</strong> Additional 2-5% based on monthly volume</p>
                    <p><strong>Leadership Bonus:</strong> Up to 10% for team building</p>
                    <p><strong>Payout Schedule:</strong> Monthly on the 1st</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>My Team Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {teamPerformance.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                        {member.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{member.name}</h3>
                        <p className="text-sm text-gray-600">{member.email}</p>
                        <p className="text-xs text-gray-500">Joined: {new Date(member.joinDate).toLocaleDateString()}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-6">
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-600">Sales</p>
                        <p className="font-bold">${member.sales.toLocaleString()}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-600">My Commission</p>
                        <p className="font-bold text-green-600">${member.commission.toLocaleString()}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-600">Team Size</p>
                        <p className="font-bold">{member.teamSize}</p>
                      </div>
                      <div className="text-center">
                        <Badge className={getLevelBadgeColor(member.level)}>
                          {member.level}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-purple-50 rounded-lg">
                <h4 className="font-semibold text-purple-900 mb-2">Team Building Rewards</h4>
                <div className="text-sm text-purple-800">
                  <p><strong>Recruit 5 active resellers:</strong> $500 bonus</p>
                  <p><strong>Achieve Silver level team:</strong> 15% commission increase</p>
                  <p><strong>Build Gold level team:</strong> Leadership bonus eligibility</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Monthly Performance Report</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span>Personal Sales</span>
                    <span className="font-semibold">$2,347.50</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span>Team Sales</span>
                    <span className="font-semibold">$15,892.25</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span>Total Commission</span>
                    <span className="font-semibold text-green-600">$3,245.75</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span>New Team Members</span>
                    <span className="font-semibold">3</span>
                  </div>
                </div>
                <Button className="w-full mt-4" variant="outline">
                  Download Full Report
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Goal Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm">Monthly Sales Goal</span>
                      <span className="text-sm font-medium">$18,240 / $25,000</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{ width: '72.96%' }}></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm">Team Building Goal</span>
                      <span className="text-sm font-medium">47 / 50</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-purple-600 h-2 rounded-full" style={{ width: '94%' }}></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm">Platinum Level Progress</span>
                      <span className="text-sm font-medium">Gold → Platinum</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-yellow-600 h-2 rounded-full" style={{ width: '65%' }}></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Marketing Tools Tab */}
        <TabsContent value="tools" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Marketing Materials</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Button variant="outline" className="w-full justify-start">
                    <Briefcase className="h-4 w-4 mr-2" />
                    Product Brochures
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <Target className="h-4 w-4 mr-2" />
                    Social Media Kit
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <Award className="h-4 w-4 mr-2" />
                    Email Templates
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <Users className="h-4 w-4 mr-2" />
                    Presentation Slides
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Referral Links</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Your Referral Link</Label>
                    <div className="flex mt-2">
                      <input 
                        type="text" 
                        value="https://cardmaster.com/ref/USER123"
                        className="flex-1 p-2 border rounded-l text-sm"
                        readOnly
                      />
                      <Button className="rounded-l-none">Copy</Button>
                    </div>
                  </div>
                  <div className="bg-green-50 p-3 rounded">
                    <p className="text-sm text-green-800">
                      <strong>Share your link and earn:</strong><br/>
                      • 25% commission on direct sales<br/>
                      • Multi-level bonuses on team sales<br/>
                      • Leadership rewards for team building
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MLMDashboard;